import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';

interface Props { autoRun: boolean; autoNonce: number; }

export function ViewportTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    innerW: window.innerWidth,
    innerH: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
    orientation: screen.orientation?.type || 'unknown',
    visualViewport: !!window.visualViewport,
    safeAreaSupported: false,
    resizeCount: 0,
    orientationChanges: 0,
  });
  const autoDoneRef = useRef(false);

  function checkSafeArea(): boolean {
    try {
      const el = document.createElement('div');
      el.style.paddingTop = 'env(safe-area-inset-top, 0px)';
      document.body.appendChild(el);
      const computed = getComputedStyle(el).paddingTop;
      document.body.removeChild(el);
      return computed !== '';
    } catch { return false; }
  }

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setMetrics({
      innerW: window.innerWidth, innerH: window.innerHeight,
      dpr: window.devicePixelRatio || 1,
      orientation: screen.orientation?.type || 'unknown',
      visualViewport: !!window.visualViewport,
      safeAreaSupported: false, resizeCount: 0, orientationChanges: 0,
    });
  }

  function start() {
    reset();
    setRunning(true);
    setMetrics((m) => ({
      ...m,
      safeAreaSupported: checkSafeArea(),
      innerW: window.innerWidth,
      innerH: window.innerHeight,
    }));
  }

  function stop() {
    setRunning(false);
    let sc = 50;
    if (metrics.visualViewport) sc += 15;
    if (metrics.safeAreaSupported) sc += 10;
    if (metrics.dpr >= 2) sc += 10;
    if (screen.orientation) sc += 10;
    sc = Math.min(100, sc + 5); // base bonus for running

    const testId: TestId = 'mobile-viewport';
    reportResult(testId, {
      score: sc,
      primary: `${metrics.innerW}x${metrics.innerH} DPR:${metrics.dpr}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  useEffect(() => {
    if (!running) return;
    function onResize() {
      setMetrics((m) => ({
        ...m,
        innerW: window.innerWidth,
        innerH: window.innerHeight,
        resizeCount: m.resizeCount + 1,
      }));
    }
    function onOrientationChange() {
      setMetrics((m) => ({
        ...m,
        orientation: screen.orientation?.type || 'unknown',
        orientationChanges: m.orientationChanges + 1,
      }));
    }
    window.addEventListener('resize', onResize);
    screen.orientation?.addEventListener('change', onOrientationChange);
    return () => {
      window.removeEventListener('resize', onResize);
      screen.orientation?.removeEventListener('change', onOrientationChange);
    };
  }, [running]);

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 3000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  return (
    <TestCard title={t('mobile.viewport.title', language)} description={t('mobile.viewport.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('viewport.width', language)} value={String(metrics.innerW)} />
        <MetricBox label={t('viewport.height', language)} value={String(metrics.innerH)} />
        <MetricBox label={t('viewport.dpr', language)} value={String(metrics.dpr)} hint={metrics.dpr >= 2 ? 'HiDPI' : 'Standard'} />
        <MetricBox label={t('viewport.orientation', language)} value={metrics.orientation} />
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-2.5 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('viewport.visualApi', language)} value={metrics.visualViewport ? t('supported.yes', language) : t('supported.no', language)} />
        <MetricBox label={t('viewport.safeArea', language)} value={metrics.safeAreaSupported ? t('supported.yes', language) : t('supported.no', language)} />
        <MetricBox label="Resize events" value={String(metrics.resizeCount)} hint="Rotate device to test" />
        <MetricBox label="Orientation changes" value={String(metrics.orientationChanges)} />
      </div>
      {running && (
        <p className="text-xs text-muted mt-3">
          {language === 'pl'
            ? 'Obróć urządzenie, zmień orientację aby zmierzyć reaktywność.'
            : 'Rotate the device, change orientation to measure responsiveness.'}
        </p>
      )}
    </TestCard>
  );
}
